import React, { type MouseEventHandler } from 'react';
import cx from 'classnames';
import {
  CircleCheckFill,
  CircleExclamationFill,
  CircleInfoFill,
  Close,
  TriangleExclamationFill,
} from '@design-systems/icons';
import useBreakpoints from '@mc/hooks/useBreakpoints';
import { useDsTranslateMessage } from '@mc/wink/internationalization/useDsTranslateMessage';
import ClusterLayout from '../ClusterLayout';
import StackLayout from '../StackLayout';
import IconButton from '../IconButton';
import Portal from '../Portal';
import Text from '../Text';
import stylesheet from './FeedbackBlock.css';

type PortalWrapperProps = {
  children?: React.ReactNode;
  condition?: boolean;
};

const PortalWrapper = ({ condition, children }: PortalWrapperProps) => {
  // @ts-expect-error TS(2322) FIXME: Type '{ children: ReactNode; prependNode: true; }'... Remove this comment to see the full error message
  return condition ? children : <Portal prependNode>{children}</Portal>;
};

type FeedbackIconProps = {
  variant?: string;
};

const FeedbackIconRedesign = ({ variant }: FeedbackIconProps) => {
  const icon =
    variant === 'success' ? (
      <CircleCheckFill color="var(--color-ui-positive)" />
    ) : variant === 'warning' ? (
      <CircleExclamationFill color="var(--color-ui-attention)" />
    ) : variant === 'error' ? (
      <TriangleExclamationFill color="var(--color-ui-negative)" />
    ) : (
      <CircleInfoFill color="var(--color-ui-neutral)" />
    );
  return icon;
};

type ContentLayoutProps = {
  children?: React.ReactNode;
};

const ContentLayout = ({ children }: ContentLayoutProps) => {
  const { isPhone } = useBreakpoints();

  // Stack actions and content for responsive view and break at the same time.

  return isPhone ? (
    <StackLayout gap={2} className={stylesheet.content}>
      {children}
    </StackLayout>
  ) : (
    <ClusterLayout
      gap={2}
      justifyContent="space-between"
      className={stylesheet.content}
      nowrap
    >
      {children}
    </ClusterLayout>
  );
};

export type FeedbackBlockProps = {
  /** Actions passed through with the TextButton or Link component */
  actions?: React.ReactNode;
  /** Copy that follows the bolded title of the feedback */
  children?: React.ReactNode;
  className?: string;
  /** Display inline if true. By default, the Feedback Block will append to the top of the page. */
  inline?: boolean;
  /** Function to dismiss FeedbackBlock */
  onClose?: MouseEventHandler<HTMLElement>;
  /** Bolded title */
  title: React.ReactNode;
  /** The different variants of FeedbackBlock */
  variant?: 'warning' | 'default' | 'success' | 'error';
};

const FeedbackBlock = React.forwardRef<HTMLDivElement, FeedbackBlockProps>(
  function FeedbackBlock(
    {
      actions,
      className,
      children,
      onClose,
      inline = false,
      title,
      variant = 'default',
      ...rest
    },
    forwardedRef,
  ) {
    const classList = cx(stylesheet.root, stylesheet[variant], className);
    const { isPhone } = useBreakpoints();

    // Translation for default icon button label text
    const iconButtonLabelText = useDsTranslateMessage({
      id: 'mcds.feedback_block.icon_button_label_text',
      defaultMessage: 'Dismiss alert',
    });

    return (
      <PortalWrapper condition={inline}>
        <ClusterLayout
          gap={3}
          nowrap
          className={classList}
          ref={forwardedRef}
          {...rest}
        >
          <span className={stylesheet.icon}>
            <FeedbackIconRedesign variant={variant} />
          </span>
          <ContentLayout>
            <div className={stylesheet.copy}>
              <Text appearance="small-bold">{title}</Text>
              <Text as="span" appearance="small-secondary">
                {children}
              </Text>
            </div>
            {actions && (
              <ClusterLayout
                gap={2}
                nowrap={!isPhone}
                justifyContent={!isPhone ? 'flex-end' : 'flex-start'}
                className={stylesheet.actions}
              >
                {actions}
              </ClusterLayout>
            )}
          </ContentLayout>
          {onClose && (
            <div>
              <IconButton
                label={iconButtonLabelText}
                icon={<Close size={isPhone ? 'small' : 'xsmall'} />}
                onClick={onClose}
              />
            </div>
          )}
        </ClusterLayout>
      </PortalWrapper>
    );
  },
);

export default FeedbackBlock;
